import { useMemo, useState } from "react";

interface SortOption<T> {
  label: string;
  compare: (a: T, b: T) => number;
}

export function useCategoryFilter<T>(
  items: T[],
  getValues: (item: T) => Record<string, string[]>,
  sortOptions: SortOption<T>[],
  pageSize = 8,
) {
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [sort, setSort] = useState(sortOptions[0].label);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const base = items.filter((item) => {
      const values = getValues(item);
      return Object.entries(selected).every(([key, chosen]) => {
        if (!chosen.length) return true;
        const itemValues = values[key] ?? [];
        return chosen.some((value) => itemValues.includes(value));
      });
    });
    const compare = sortOptions.find((option) => option.label === sort)?.compare;
    return compare ? [...base].sort(compare) : base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, selected, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const toggle = (key: string, value: string) => {
    setPage(1);
    setSelected((prev) => {
      const current = prev[key] ?? [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [key]: next };
    });
  };

  const reset = () => {
    setSelected({});
    setPage(1);
  };

  const changeSort = (value: string) => {
    setSort(value);
    setPage(1);
  };

  return {
    selected,
    toggle,
    reset,
    sort,
    setSort: changeSort,
    page: safePage,
    setPage,
    pageItems,
    totalPages,
    total: filtered.length,
  };
}